// ─────────────────────────────────────────────────────────────────────────────
// journal_client.ts — Typed facade for the journal control API
//
// Two implementations satisfy the same `IJournalClient` shape:
//   - HttpJournalClient (this file): hits run_pwa.py over fetch()
//   - FsaJournalClient (fsa_journal_client.ts): drives a File System Access
//     directory handle, replicating the server's on-disk layout in-browser
//
// `standalone_host.init()` picks one at boot. The HTTP path tolerates a
// missing server: probe() returns null rather than throwing, letting the
// host fall back to FSA (or IDB) when the PWA is launched without
// run_pwa.py behind it.
//
// Endpoint map — see run_pwa.py docstring for full contract:
//
//   GET  /api/journal                  → JournalInfo
//   GET  /api/tree[?depth=N]         → TreeNode (root, children recursively)
//   GET  /api/page/read?id=<path>    → PageReadResponse
//   POST /api/page/save              → { ok, id, driver, bytes, sidecars[] }
//   GET  /api/session                → Record<string, unknown>
//   POST /api/session                → { ok }
// ─────────────────────────────────────────────────────────────────────────────

// ── Response shapes ─────────────────────────────────────────────────────────

export interface JournalDriverInfo {
  name: string;
  extensions: string[];
  kind: string;
  lossy: boolean;
}

export interface JournalInfo {
  root: string;
  dotOx: string;
  title: string;
  /** True iff this open() call materialised a fresh header (new journal).
   *  False for subsequent opens of an existing folder. */
  createdFresh: boolean;
  createdAt: number;
  manifestVersion: number;
  levelLabels: Record<string, string>;
  reservedDirs: string[];
  drivers: JournalDriverInfo[];
}

export interface TreeNode {
  /** Forward-slash relative path from journal root. Empty string on the root node. */
  id: string;
  title: string;
  /** 0 = root, 1 = collection, 2 = chapter, 3+ = section/subsection. */
  level: number;
  /** Human-readable depth label from the journal manifest ("collection", "chapter", …). */
  levelLabel: string;
  isContainer: boolean;
  /** Driver name (from journal registry) — null for containers. */
  driver: string | null;
  children?: TreeNode[];
}

export interface PageReadResponse {
  id: string;
  driver: string;
  lossy: boolean;
  blocks: unknown[];
  strokes: unknown[];
  /** Arrow connectors between blocks. Returned at the top level by the
   *  native-json driver; absent (or empty) for lossy drivers. */
  arrows?: unknown[];
  meta: Record<string, unknown>;
}

export interface PageSaveRequest {
  id: string;
  blocks: unknown[];
  strokes: unknown[];
  /** Arrow connectors. Persisted only by the native-json driver. */
  arrows?: unknown[];
  meta?: Record<string, unknown>;
  /** Allow creating the file (and its parent directories) if it doesn't exist. */
  create?: boolean;
}

export interface PageSaveResponse {
  ok: true;
  id: string;
  driver: string;
  bytes: number;
  sidecars: string[];
  created?: boolean;
}

/** Per-folder landing-page metadata. Persisted in `<folder>/.ox.folder.json`
 *  for non-root folders, `<root>/.ox/folder.json` for the journal root. */
export interface FolderInfo {
  /** Forward-slash relative path from journal root. Empty string for root. */
  path: string;
  title: string;
  description: string;
}

// ── Errors ─────────────────────────────────────────────────────────────────

/** Thrown by methods that need a live journal when /api/journal was not reachable. */
export class JournalUnavailableError extends Error {
  constructor(message = 'journal not reachable') {
    super(message);
    this.name = 'JournalUnavailableError';
  }
}

// ── Interface ──────────────────────────────────────────────────────────────

/** Contract shared by HttpJournalClient (run_pwa.py-backed) and
 *  FsaJournalClient (FileSystemDirectoryHandle-backed). standalone_host
 *  consumes this interface so the same `_initFromJournal` lifecycle drives
 *  both modes. */
export interface IJournalClient {
  probe(): Promise<JournalInfo | null>;
  info(): Promise<JournalInfo>;
  tree(opts?: { path?: string; depth?: number }): Promise<TreeNode>;
  readPage(id: string): Promise<PageReadResponse>;
  savePage(req: PageSaveRequest): Promise<PageSaveResponse>;
  /** Remove the page and any sidecars (ink, etc.). Optional —
   *  implementations that can't delete (HTTP server without the
   *  endpoint) may resolve as a no-op, in which case the deletion
   *  is in-memory only and files orphan on disk until the user
   *  manually cleans up. The reducer treats failure as non-fatal. */
  deletePage?(id: string): Promise<void>;
  getFolderInfo(path?: string): Promise<FolderInfo>;
  setFolderInfo(info: { path?: string; title?: string; description?: string }): Promise<FolderInfo>;
  loadSession(): Promise<Record<string, unknown>>;
  saveSession(session: Record<string, unknown>): Promise<void>;
}

// ── HTTP Client ────────────────────────────────────────────────────────────

export class JournalClient implements IJournalClient {
  /** Base URL, empty string = same-origin. Trailing slash is stripped. */
  private readonly _base: string;

  constructor(baseUrl: string = '') {
    this._base = baseUrl.replace(/\/+$/, '');
  }

  // ── Probes ────────────────────────────────────────────────────────────

  /**
   * Fetch journal info, or null if the server is unreachable / no journal open.
   * This is the main "are we running against a live journal?" signal — boot
   * calls this once and branches.
   */
  async probe(): Promise<JournalInfo | null> {
    try {
      const res = await fetch(this._url('/api/journal'), { cache: 'no-store' });
      if (!res.ok) return null;
      const data = (await res.json()) as Partial<JournalInfo> & { error?: string };
      if (data.error || !data.root) return null;
      return data as JournalInfo;
    } catch {
      return null;
    }
  }

  // ── Reads ─────────────────────────────────────────────────────────────

  async info(): Promise<JournalInfo> {
    const res = await this._get('/api/journal');
    return (await res.json()) as JournalInfo;
  }

  /** Fetch a folder subtree. Both args optional:
   *    - `path`  forward-slash relative folder id; empty/undefined = journal root
   *    - `depth` how many levels below the start to scan; default 16, pass 1
   *              for "just this folder's immediate children" (lazy nav).
   */
  async tree(opts: { path?: string; depth?: number } = {}): Promise<TreeNode> {
    const params = new URLSearchParams();
    if (opts.path) params.set('path', opts.path);
    if (opts.depth != null) params.set('depth', String(opts.depth | 0));
    const qs = params.toString();
    const url = qs ? `/api/tree?${qs}` : '/api/tree';
    const res = await this._get(url);
    return (await res.json()) as TreeNode;
  }

  async readPage(id: string): Promise<PageReadResponse> {
    const res = await this._get(`/api/page/read?id=${encodeURIComponent(id)}`);
    return (await res.json()) as PageReadResponse;
  }

  // ── Folder landing-page metadata ─────────────────────────────────────

  async getFolderInfo(path: string = ''): Promise<FolderInfo> {
    const qs = path ? `?path=${encodeURIComponent(path)}` : '';
    const res = await this._get(`/api/folder/info${qs}`);
    return (await res.json()) as FolderInfo;
  }

  async setFolderInfo(info: { path?: string; title?: string; description?: string }): Promise<FolderInfo> {
    const res = await this._post('/api/folder/info', info);
    return (await res.json()) as FolderInfo;
  }

  // ── Writes ────────────────────────────────────────────────────────────

  async savePage(req: PageSaveRequest): Promise<PageSaveResponse> {
    const res = await this._post('/api/page/save', req);
    return (await res.json()) as PageSaveResponse;
  }

  // ── Session (per-journal) ───────────────────────────────────────────────

  async loadSession(): Promise<Record<string, unknown>> {
    try {
      const res = await fetch(this._url('/api/session'), { cache: 'no-store' });
      if (!res.ok) return {};
      const data = await res.json();
      return (data && typeof data === 'object' && !('error' in data)) ? data : {};
    } catch {
      return {};
    }
  }

  async saveSession(session: Record<string, unknown>): Promise<void> {
    await this._post('/api/session', session);
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private _url(path: string): string {
    return this._base + path;
  }

  private async _get(path: string): Promise<Response> {
    const res = await fetch(this._url(path), { cache: 'no-store' });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`GET ${path} failed: ${res.status} ${body}`);
    }
    return res;
  }

  private async _post(path: string, body: unknown): Promise<Response> {
    const res = await fetch(this._url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      throw new Error(`POST ${path} failed: ${res.status} ${errBody}`);
    }
    return res;
  }
}

// ── Tree helpers ───────────────────────────────────────────────────────────

/** Yield every leaf page (file-backed node) in the tree, depth-first. */
export function* walkPageLeaves(node: TreeNode): Generator<TreeNode> {
  if (!node.isContainer) {
    yield node;
    return;
  }
  for (const child of node.children ?? []) {
    yield* walkPageLeaves(child);
  }
}

/** Yield every container node (folders → collections/chapters/sections), depth-first. */
export function* walkContainers(node: TreeNode): Generator<TreeNode> {
  if (node.isContainer) yield node;
  for (const child of node.children ?? []) {
    if (child.isContainer) yield* walkContainers(child);
  }
}

/** Flatten the tree into an ordered list of page leaves — this becomes the
 *  linear page list the notebook shows. Order matches depth-first traversal
 *  of the sorted tree (journal.py already sorts directories first then files). */
export function flattenPages(root: TreeNode): TreeNode[] {
  return Array.from(walkPageLeaves(root));
}

// ── Singleton (same-origin default) ────────────────────────────────────────

/** Process-wide HTTP client bound to same-origin. Callers that need a
 *  custom base URL construct their own JournalClient. */
export const journalClient = new JournalClient();

/** Friendly alias — clarifies the dispatch flavour at call sites. */
export { JournalClient as HttpJournalClient };
